import type { SynergyFigure } from '../../synergy-risk';
import { ASK_VIEW_STATUS_LABELS, type AskViewThesisStatus } from './view-payload';

export type RecoveryOutcomeReason = 'no_governed_actuals' | 'actuals_unavailable'
  | 'period_not_closed'
  | 'attribution_missing' | 'population_mismatch' | 'currency_mismatch' | 'month_missing';
export type RecoveryOutcomeVerdict = 'worked' | 'partly' | 'did_not_work'
  | 'too_early' | 'not_measurable' | 'actuals_unavailable';

export const RECOVERY_OUTCOME_STATUS = {
  worked: 'on_track', partly: 'at_risk', did_not_work: 'off_track',
  actuals_unavailable: 'not_yet_measurable',
  too_early: 'not_yet_measurable', not_measurable: 'not_yet_measurable',
} as const satisfies Readonly<Record<RecoveryOutcomeVerdict, AskViewThesisStatus>>;

export function recoveryOutcomeStatusWord(verdict: RecoveryOutcomeVerdict):
  typeof ASK_VIEW_STATUS_LABELS[typeof RECOVERY_OUTCOME_STATUS[RecoveryOutcomeVerdict]] {
  return ASK_VIEW_STATUS_LABELS[RECOVERY_OUTCOME_STATUS[verdict]];
}

/** API-authored gross outcome; amounts retain their governed engine receipts. */
export type AskViewRecoveryOutcome = Readonly<{
  title: 'Did it work?';
  decisionId: string;
  leverLabel: string;
  checkpoint: string;
  statusWord: ReturnType<typeof recoveryOutcomeStatusWord>;
  basis: 'gross';
  expected: SynergyFigure | null;
  actual: SynergyFigure | null;
  delta: SynergyFigure | null;
  reasons: readonly RecoveryOutcomeReason[];
  disclosure: string;
  computationRowKeys: readonly string[];
  deliveryNote: string | null;
}>;


export const RECOVERY_OUTCOME_REASON_LABELS = {
  no_governed_actuals: 'No governed actuals are available.',
  actuals_unavailable: 'Actuals could not be read. Try again later.',
  period_not_closed: 'The checkpoint period is not closed.',
  attribution_missing: 'Actuals are not attributed to this decision.',
  population_mismatch: 'Actuals and baseline cover different spending.',
  currency_mismatch: 'Actuals and baseline currencies do not match.',
  month_missing: 'A required month is missing.',
} as const satisfies Readonly<Record<RecoveryOutcomeReason, string>>;
